import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  tokenTargetOf,
  usesTokens,
  type FortressCard,
  isBuildingPatternAsymmetric,
} from "@fortress/shared";
import {
  DiscountedNumber,
  ResourceCost,
  useObsidianCost,
  usePlayerCount,
  withPerPlayerFigures,
} from "../costDisplay";
import { ResourceIcon } from "../resources";
import { MirrorPair, PatternDiagram, rotatePattern } from "./fortressPattern";
import {
  parseCardText,
  withoutRestrictionSentences,
  type ParsedCardText,
  type PassivePart,
} from "./cardText";
import { IconLineView, OrRule, TokenTray, type TraySpec } from "./cardIcons";
import "./fortressCard.css";

// The full visual of a single Fortress card — shared by the read-only catalogue
// (`FortressLibrary`), the card editor, match history and the in-game detail
// popover (`App`), so a card renders the same way everywhere.
//
// Layout: an ink crest (gold on win cards) with the name and Obsidian cost, the
// build pattern (and its mirror) full-bleed, then the effects — the Action with
// its token tray, a rule, and the Passive (drawn as icons where the printed
// phrasing allows; see cardText.ts / cardIcons.tsx). Regular cards have a cream
// face, Win cards an ink one, so the two decks read apart at a glance.

// Category colour schemes, shared by the sidebar sections (App) and the library
// headings. Win = grey/black, Regular = light grey.
export const WIN_ACCENT = "#4a4a52";
export const REGULAR_ACCENT = "#c7c7cf";

/** When a win card's condition is checked, for the timings that need saying.
 * "immediate" (the default) shows nothing — a win the moment it's met. */
const WIN_TIMING: Partial<Record<string, { label: string; title: string; icon: string }>> = {
  "end-of-round": {
    label: "Wins at end of round",
    title: "This win condition is checked when the round ends",
    // A moon: the round is over.
    icon: "M19.5 14.6A8 8 0 1 1 9.4 4.5a6.4 6.4 0 0 0 10.1 10.1z",
  },
  "start-of-turn": {
    label: "Wins at start of turn",
    title: "This win condition is checked at the start of a turn",
    // A sunrise: the turn begins.
    icon: "M3 19h18M6.5 19a5.5 5.5 0 0 1 11 0M12 9.5V5.5M5.4 12.6 3.9 11.1M18.6 12.6l1.5-1.5",
  },
};

/** Altitude levels (shared VIGOR_ZONES_CONFIG names). The rules count FOUR
 * energy zones: forest and foothill are one — the lowest — then ridgeline,
 * crag and summit. */
const ZONE_NAME = ["forest", "foothill", "ridgeline", "crag", "summit"];
const LOWEST_ZONE = 0;
const HIGHEST_ZONE = ZONE_NAME.length - 1;
/** The first altitude level ABOVE the lowest energy zone (ridgeline). */
const ABOVE_LOWEST_ZONE = LOWEST_ZONE + 2;
const zoneWords = (z: number) =>
  z < ABOVE_LOWEST_ZONE
    ? "the lowest energy zone"
    : z === HIGHEST_ZONE
      ? "the highest energy zone"
      : `the ${ZONE_NAME[z] ?? `level ${z}`} zone`;

/** The card's build restrictions, one sentence each: its printed
 * "Restriction:" lines when it has any, else read from the effect data's
 * placement rules and build gate (what the server enforces). */
export function cardRestrictions(
  card: FortressCard,
  parsed: ParsedCardText = parseCardText(card.description),
): { lines: string[]; printed: boolean } {
  if (parsed.restrictions?.length) return { lines: [...parsed.restrictions], printed: true };
  const lines: string[] = [];
  const pl = card.effects?.placement;
  const zone = pl?.zone;
  if (zone) {
    const { min, max, maxVigorPenalty } = zone;
    if (min !== undefined && max !== undefined && min === max) {
      lines.push(`Must be built entirely in ${zoneWords(min)}.`);
    } else if (min === HIGHEST_ZONE) {
      lines.push(`Must be built entirely in ${zoneWords(HIGHEST_ZONE)}.`);
    } else if (max !== undefined && max < ABOVE_LOWEST_ZONE) {
      lines.push(`Must be built entirely in ${zoneWords(LOWEST_ZONE)}.`);
    } else if (min === ABOVE_LOWEST_ZONE && max === undefined) {
      lines.push(`Can't be built in ${zoneWords(LOWEST_ZONE)}.`);
    } else if (min === LOWEST_ZONE + 1 && max === undefined) {
      // A pre-"50" printing that excluded the forest alone.
      lines.push("Can't be built in the forest.");
    } else if (min !== undefined || max !== undefined) {
      const parts = [
        min !== undefined ? `${zoneWords(min)} or higher` : null,
        max !== undefined ? `${zoneWords(max)} or lower` : null,
      ].filter(Boolean);
      lines.push(`Must be built entirely in ${parts.join(" and ")}.`);
    }
    if (maxVigorPenalty === 0) {
      // Every zero-penalty level — forest and foothill — IS the lowest zone.
      lines.push(`Must be built entirely in ${zoneWords(LOWEST_ZONE)}.`);
    }
  }
  // A fortress's zone is read from the nodes it occupies (buildings and X
  // markers). A single-node pattern (Hole Worm: one X, three bridges) sits in
  // exactly one zone whatever happens, so the sentence would only state the
  // obvious.
  if (pl?.sameZone && card.pattern.buildings.length + card.pattern.markers.length > 1) {
    lines.push("Must be built entirely within one energy zone.");
  }
  if (pl?.requireBuildingInZone !== undefined) {
    // Name the piece when the pattern has only one kind (Blimp-port: "At
    // least 1 scout must be in the highest energy zone").
    const kinds = new Set(card.pattern.buildings.map((b) => b.shape));
    const only = kinds.size === 1 ? [...kinds][0] : undefined;
    const piece = only && only !== "wild" ? only : "building";
    lines.push(`At least 1 ${piece} must be in ${zoneWords(pl.requireBuildingInZone)}.`);
  }
  const gate = card.effects?.gate;
  if (gate?.kind === "fortress_count_at_least") {
    lines.push(`Can only be built if you control ${gate.count} or more fortresses.`);
  } else if (gate?.kind === "mine_in_every_zone") {
    lines.push("Can only be built if you have a mine in every energy zone.");
  }
  return { lines, printed: false };
}

function RestrictionIcon() {
  // A peak with an altitude band: build rules are about energy zones.
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden className="fcard-restriction-icon">
      <path
        d="M2.5 19.5 9 8l3.6 6.2 2.4-3.9 6.5 9.2zM6.6 14.4h5.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Cracked hex: this fortress's buildings can be attacked and destroyed. */
function DestructibleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 2.8 20 7.4v9.2L12 21.2 4 16.6V7.4zM12.6 2.9 10.6 8.4l3 2.6-2.6 4.2 1.4 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The card's token tray, if it shows one: the Action's printed token code
 * ("1TR", "3T") on "49"+ printings, else (older printings, win cards) the
 * effect data's finite token cap. Null = no tray. */
export function cardTokenTray(card: FortressCard): TraySpec | null {
  const parsed = parseCardText(card.description);
  if (parsed.action?.tokens) return parsed.action.tokens;
  if (parsed.plain !== undefined && usesTokens(card.effects)) {
    // A win card's track is its win threshold ("win at N tokens" — Obsidian
    // Blimp-port's 2, Vacmak'd's 5); a use-budgeted card's is its cap.
    const cap = tokenTargetOf(card.effects);
    if (Number.isFinite(cap) && cap > 0 && cap <= 8) {
      return {
        count: cap,
        perRound: !!card.effects?.resetsEachRound?.tokens,
        win: card.effects?.winAtTokens !== undefined,
      };
    }
  }
  return null;
}

function PassiveView({ parts, dark }: { parts: readonly PassivePart[]; dark: boolean }) {
  const one = (p: PassivePart, key: number) => {
    switch (p.kind) {
      case "text":
        return (
          <p key={key} className="fcard-text">
            {p.text}
          </p>
        );
      case "icons":
        return <IconLineView key={key} line={p.line} text={p.text} dark={dark} />;
      case "choice":
        return (
          <div key={key} className="fcard-choice" role="group" aria-label="Choose one">
            {p.options.map((o, i) => (
              <div key={i} className="fcard-choice-option">
                {i > 0 && <OrRule />}
                {one(o, 0)}
              </div>
            ))}
          </div>
        );
    }
  };
  return <div className="fcard-passive">{parts.map(one)}</div>;
}

export function FortressCardFace({
  card,
  tokens = 0,
  style,
}: {
  card: FortressCard;
  /** Tokens currently on the card (an implemented card in play) — fills that
   * many of its tray's slots. */
  tokens?: number;
  /** Merged onto the card's outer container (positioning, width overrides). */
  style?: CSSProperties;
}) {
  const isWin = card.kind === "win";
  const obsidian = card.cost[3];
  // What the viewer actually pays: their own fortresses can discount a whole
  // deck (Inverse Molehill). Outside a game this is the printed price.
  const paidObsidian = useObsidianCost()(card);
  // Seated players, for the "per player" figures (undefined outside a game).
  const playerCount = usePlayerCount();
  // Horny Neighbour: the printed total is split across the seated players —
  // the crest reads "50/X", with this match's share in brackets.
  const splitCost = card.effects?.costSplitByPlayers === true;
  // Display-only pattern rotation (60° steps) — a matching aid; the board
  // matcher tries every orientation regardless.
  const [rotation, setRotation] = useState(0);
  useEffect(() => setRotation(0), [card.id]);
  const pattern = rotatePattern(card.pattern, rotation);
  const parsed = useMemo(() => parseCardText(card.description), [card.description]);
  const tray = useMemo(() => cardTokenTray(card), [card]);
  const timing = card.winTiming ? WIN_TIMING[card.winTiming] : undefined;
  const restrictions = useMemo(() => cardRestrictions(card, parsed), [card, parsed]);
  // An older one-paragraph printing states its restriction in the prose; the
  // restriction section says it now, so drop that sentence there.
  const plainText =
    parsed.plain === undefined
      ? undefined
      : withPerPlayerFigures(
          restrictions.lines.length && !restrictions.printed
            ? withoutRestrictionSentences(parsed.plain)
            : parsed.plain,
          playerCount,
        );
  const destructible = card.effects?.destructible;

  return (
    <article
      className={`fcard${isWin ? " fcard--win" : ""}`}
      style={style}
      aria-label={card.name}
    >
      <header className="fcard-crest">
        <div className="fcard-title">
          <h3 className="fcard-name">{card.name}</h3>
          {timing && (
            <span className="fcard-when" title={timing.title}>
              <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden>
                <path
                  d={timing.icon}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {timing.label}
            </span>
          )}
        </div>
        <span
          className="fcard-cost"
          title={
            splitCost
              ? "Obsidian cost: the printed total split across the seated players, rounded up"
              : "Obsidian cost"
          }
        >
          {splitCost ? (
            <>
              <ResourceIcon kind="obsidian" size={38} />
              <span className="cost-value">
                {obsidian}/X
                {playerCount ? (
                  <>
                    {" ("}
                    <DiscountedNumber
                      printed={Math.ceil(obsidian / playerCount)}
                      paid={Math.min(paidObsidian, Math.ceil(obsidian / playerCount))}
                    />
                    {")"}
                  </>
                ) : (
                  ""
                )}
              </span>
            </>
          ) : (
            <ResourceCost kind="obsidian" printed={obsidian} paid={paidObsidian} size={38} />
          )}
        </span>
      </header>

      <div className="fcard-pattern">
        {/* Only chiral patterns have a distinct mirror build orientation; for
            achiral ones the "mirror" is just a rotation, so one diagram says
            it all. */}
        {isBuildingPatternAsymmetric(pattern) ? (
          <MirrorPair pattern={pattern} dark={isWin} label={`${card.name} build pattern`} />
        ) : (
          <PatternDiagram
            pattern={pattern}
            maxW={268}
            maxH={176}
            pad={6}
            dark={isWin}
            label={`${card.name} build pattern`}
          />
        )}
        {destructible && (
          <span
            className="fcard-destructible"
            title={`Destructible — its buildings can be attacked and destroyed, which unravels the fortress${
              destructible.returnToPile ? " and returns this card to the pile" : ""
            }`}
          >
            <DestructibleIcon />
            Destructible
          </span>
        )}
        <button
          type="button"
          className="fcard-rotate"
          aria-label="Rotate the pattern"
          title="Rotate the pattern 60° — a viewing aid; any orientation matches on the board"
          onClick={() => setRotation((r) => (r + 1) % 6)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <path
              d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3M19.5 4.5V8H16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="fcard-effects">
        {restrictions.lines.length > 0 && (
          <div className="fcard-restrictions" role="group" aria-label="Restrictions">
            {restrictions.lines.map((r, i) => (
              <p key={i} className="fcard-restriction">
                <RestrictionIcon />
                <span>{r}</span>
              </p>
            ))}
          </div>
        )}
        {plainText !== undefined ? (
          <div className="fcard-block">
            {/* Pre-"49" printings: one paragraph, kept verbatim. */}
            {plainText && <p className="fcard-text is-plain">{plainText}</p>}
            {tray && <TokenTray spec={tray} filled={tokens} dark={isWin} />}
          </div>
        ) : (
          <>
            {parsed.action && (
              <div className="fcard-block">
                {parsed.action.options ? (
                  // Pick-one action: one option per row; each later option
                  // opens with the passive's "— or —", in line with its text.
                  <div className="fcard-choice" role="group" aria-label="Choose one">
                    {parsed.action.options.map((o, i) => (
                      <p key={i} className="fcard-text">
                        {i === 0 ? (
                          <strong className="fcard-keyword">Action: </strong>
                        ) : (
                          <OrRule />
                        )}
                        {o}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="fcard-text">
                    <strong className="fcard-keyword">Action:</strong>{" "}
                    {withPerPlayerFigures(parsed.action.text, playerCount)}
                  </p>
                )}
                {tray && <TokenTray spec={tray} filled={tokens} dark={isWin} />}
              </div>
            )}
            {parsed.action && parsed.passive && <hr className="fcard-rule" />}
            {parsed.passive && <PassiveView parts={parsed.passive} dark={isWin} />}
          </>
        )}
      </div>
    </article>
  );
}
